import type { AudioService } from "../audio/ports/AudioService";
import type { MoneyFormatter } from "../money/MoneyFormatter";
import type { FeatureModeStore } from "../feature-mode/FeatureModeStore";
import type { BetSystem } from "./BetSystemTypes";
import type { BetPort } from "./BetPort";
import type { SpinCoordinator } from "../spin/SpinCoordinator";
import { multiplyMinorByMultiplier } from "../money/multiplyMinorByMultiplier";

export class BetPresenter {
  private readonly panel: BetPort;
  private readonly audioService: AudioService;
  private readonly formatter: MoneyFormatter;
  private readonly featureModeStore: FeatureModeStore;
  private readonly featureModeMultipliersById: Readonly<Record<string, number>>;
  private readonly betSystem: BetSystem;
  private readonly spinCoordinator: SpinCoordinator;

  public constructor(input: {
    panel: BetPort;
    betSystem: BetSystem;
    audioService: AudioService;
    formatter: MoneyFormatter;
    featureModeStore: FeatureModeStore;
    /** Operator setting: per-mode bet multipliers (`initialSettings.featureModeMultipliersById`). */
    featureModeMultipliersById: Readonly<Record<string, number>>;
    spinCoordinator: SpinCoordinator;
    onBeforeBetChange?: () => void;
  }) {
    this.panel = input.panel;
    this.betSystem = input.betSystem;
    this.spinCoordinator = input.spinCoordinator;
    this.audioService = input.audioService;
    this.formatter = input.formatter;
    this.featureModeStore = input.featureModeStore;
    this.featureModeMultipliersById = input.featureModeMultipliersById;

    this.panel.bind({
      onMinus: () => {
        input.onBeforeBetChange?.();
        this.decrease();
      },
      onPlus: () => {
        input.onBeforeBetChange?.();
        this.increase();
      },
    });
  }

  /** One bet step down with its cue; a no-op while the bet cannot change. */
  public decrease(): void {
    const s = this.spinCoordinator.getSnapshot();
    if (!s.canChangeBet) return;

    this.betSystem.decreaseBet();
    this.audioService.presentation.emit({ id: "bet.decrease" });
    this.render();
  }

  /** One bet step up with its cue; a no-op while the bet cannot change. */
  public increase(): void {
    const s = this.spinCoordinator.getSnapshot();
    if (!s.canChangeBet) return;

    this.betSystem.increaseBet();
    this.audioService.presentation.emit({ id: "bet.increase" });
    this.render();
  }

  public render(): void {
    const snap = this.spinCoordinator.getSnapshot();
    const betSnapshot = this.betSystem.getSnapshot();

    this.panel.setLocked(!snap.canChangeBet);

    const displayedBetMinor = this.getDisplayedBetMinor();
    const text = this.formatter.formatMinor(displayedBetMinor);
    this.panel.setBetText(text);

    const progress =
      betSnapshot.stepCount <= 1
        ? 1
        : (betSnapshot.currentStepIndex + 1) / betSnapshot.stepCount;

    this.panel.setBetProgress(progress);

    const locked = !snap.canChangeBet;
    const canDec = !locked && this.betSystem.canDecrease();
    const canInc = !locked && this.betSystem.canIncrease();

    this.panel.setMinusEnabled(canDec);
    this.panel.setPlusEnabled(canInc);
  }

  private getDisplayedBetMinor(): string {
    const baseBetMinor = this.betSystem.getCurrentBetMinor();
    const activeFeatureMode = this.featureModeStore.getSnapshot().activeMode;

    if (!activeFeatureMode) {
      return baseBetMinor;
    }

    const multipliersById = this.featureModeMultipliersById;
    const multiplier = multipliersById[activeFeatureMode.id];

    if (typeof multiplier !== "number" || !Number.isFinite(multiplier) || multiplier <= 0) {
      return baseBetMinor;
    }

    return multiplyMinorByMultiplier(baseBetMinor, multiplier);
  }
}